import React from 'react';
import { AbsenceCodesSettings } from '../Planning/AbsenceCodesSettings';
import { useLocation } from 'react-router-dom';
import { Settings, User as UserIcon, Receipt, CalendarClock, Clock, Bell, Plus, Trash2, Loader2, Plug, FileText, X, Globe, Code2, Copy, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi, planningApi, moduleApi } from '../../lib/api';
import { BillingSettingsModal } from '../Factures/BillingSettingsModal';
import { PrestationNotificationsSettings } from '../PrestationPlanning/PrestationNotificationsPage';
import { ClientEmailNotificationsSettings } from './ClientEmailNotificationsSettings';
import { ApplicationsSettings } from './ApplicationsSettings';
import { DocumentTemplatesSettings } from './DocumentTemplatesSettings';
import { WeeklyHours, DayKey, DAY_KEYS, DAY_LABELS, defaultWeeklyHours } from '../PrestationPlanning/prestationPlanningShared';

// --- Onglet Compte : email + mot de passe (même logique que l'ancienne modale "Mon profil" de App.tsx,
// simplement déplacée ici) ---
export function AccountTab() {
    const { user, refreshUser } = useAuth();
    const [form, setForm] = React.useState({ email: user?.email || '', currentPassword: '', newPassword: '', confirmPassword: '' });
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');
    const [saving, setSaving] = React.useState(false);

    const isEmploye = user?.type === 'employe';
    const emailChanged = (form.email || '').trim() !== String(user?.email || '').trim();
    const wantsPasswordChange = form.newPassword.length > 0 || form.confirmPassword.length > 0;
    const isEmailRequired = user?.type !== 'collaborator' && !isEmploye;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailError = form.email.trim()
        ? (!emailPattern.test(form.email.trim()) ? 'Format email invalide.' : '')
        : (isEmailRequired ? 'Email obligatoire pour ce compte.' : '');
    const passwordError = wantsPasswordChange && form.newPassword.length < 8
        ? 'Le mot de passe doit contenir au moins 8 caractères.'
        : '';
    const confirmError = wantsPasswordChange && form.newPassword !== form.confirmPassword
        ? 'La confirmation du mot de passe ne correspond pas.'
        : '';
    const currentPasswordError = wantsPasswordChange && !form.currentPassword
        ? 'Mot de passe actuel requis pour changer le mot de passe.'
        : '';
    const hasChanges = emailChanged || wantsPasswordChange;
    const canSave = hasChanges && !emailError && !passwordError && !confirmError && !currentPasswordError;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!canSave) return;
        try {
            setSaving(true);
            const payload = {
                newEmail: (form.email || '').trim(),
                currentPassword: form.currentPassword || undefined,
                newPassword: form.newPassword || undefined,
            };
            if (isEmploye) await authApi.updateEmployeeProfile(payload);
            else await authApi.updateProfile(payload);
            await refreshUser();
            setSuccess('Profil mis à jour avec succès.');
            setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la mise à jour du profil.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
            {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
            {success && <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">{success}</div>}

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors"
                    placeholder="nom@exemple.com" />
                {emailError && <p className="text-xs text-red-400">{emailError}</p>}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Mot de passe actuel</label>
                <input type="password" value={form.currentPassword} onChange={(e) => setForm((p) => ({ ...p, currentPassword: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors"
                    placeholder="Requis si changement de mot de passe" />
                {currentPasswordError && <p className="text-xs text-red-400">{currentPasswordError}</p>}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nouveau mot de passe</label>
                <input type="password" value={form.newPassword} onChange={(e) => setForm((p) => ({ ...p, newPassword: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors"
                    placeholder="Minimum 8 caractères" />
                {passwordError && <p className="text-xs text-red-400">{passwordError}</p>}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Confirmer le nouveau mot de passe</label>
                <input type="password" value={form.confirmPassword} onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors" />
                {confirmError && <p className="text-xs text-red-400">{confirmError}</p>}
            </div>

            <button type="submit" disabled={!canSave || saving}
                className="px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
        </form>
        <BookingWidgetSection />
        </>
    );
}

// --- Widget "Réserver en ligne" (bouton flottant à intégrer sur le site du propriétaire) — regroupé dans
// "Compte" plutôt que dans "Application" (qui ne concerne que des outils tiers connectés, pas un widget
// propre à L'IAmani). Trois cas : client Prestation (catalogue), client Restauration avec RDV Pro actif, et
// super admin (widget personnel pour démarcher de potentiels futurs clients — bascule + libellé de bouton
// personnalisable, puisqu'il n'a pas de module "rdv_pro" à activer/désactiver comme un établissement).
function WidgetCard({ title, description, snippet, copied, onCopy }: { title: string; description: string; snippet: string; copied: boolean; onCopy: () => void }) {
    return (
        <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-3">
            <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                    <Globe size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-content text-sm">{title}</p>
                    <p className="text-xs text-content-muted mt-0.5">{description}</p>
                </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-2">
                <Code2 size={14} className="text-content-muted shrink-0" />
                <code className="text-[11px] text-content-muted break-all font-mono flex-1">{snippet}</code>
            </div>
            <p className="text-xs text-content-muted">Collez ce code juste avant la balise <code className="text-[11px] bg-surface border border-border px-1 py-0.5 rounded">&lt;/body&gt;</code> de votre site.</p>
            <button onClick={onCopy} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                {copied ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier le code</>}
            </button>
        </div>
    );
}

function BookingWidgetSection() {
    const { user } = useAuth();
    const ownerId = user?.type === 'collaborator' ? ((user as any)?.client_id || '') : (user?.id || '');
    const isAdmin = user?.type === 'admin';
    const isPrestation = user?.type === 'client' && (user as any)?.client_type === 'prestation';
    const isRestauration = user?.type === 'client' && (user as any)?.client_type === 'restauration';

    const [hasRdvPro, setHasRdvPro] = React.useState(false);
    const [hasPlanningRdv, setHasPlanningRdv] = React.useState(false);
    React.useEffect(() => {
        if (!isRestauration && !isPrestation) return;
        authApi.getMyModules().then((modules: any) => {
            const names = (Array.isArray(modules) ? modules : []).filter((m: any) => m.is_active === 1).map((m: any) => m.module_name);
            setHasRdvPro(names.includes('rdv_pro'));
            setHasPlanningRdv(names.includes('planning_rdv'));
        }).catch(() => { setHasRdvPro(false); setHasPlanningRdv(false); });
    }, [isRestauration, isPrestation]);

    // Bascule + libellé du widget RDV — réutilise directement /pro-rdv/settings, partagé par le super admin
    // (widget de démarchage), un établissement Restauration (RDV Pro) et un établissement Prestation (agenda
    // salariés, widget multi-Démarches) — même contrôle pour les trois, résolu côté serveur selon le compte
    // connecté. Sans cette bascule accessible, `pro_rdv_widget_enabled` restait bloqué à 0 par défaut pour un
    // client Prestation dès qu'une ligne client_settings existait pour lui (ex: horaires Planning déjà
    // configurés) — le lien "Envoyer un lien de rendez-vous" échouait alors toujours avec "non disponible".
    const canControlRdvWidget = isAdmin || (isRestauration && hasRdvPro) || (isPrestation && hasPlanningRdv);
    const [rdvWidgetEnabled, setRdvWidgetEnabled] = React.useState(false);
    const [rdvWidgetLabel, setRdvWidgetLabel] = React.useState('');
    const [rdvWidgetLoading, setRdvWidgetLoading] = React.useState(true);
    const [rdvWidgetSaving, setRdvWidgetSaving] = React.useState(false);
    React.useEffect(() => {
        if (!canControlRdvWidget) { setRdvWidgetLoading(false); return; }
        moduleApi.getProRdvSettings().then((s: any) => {
            setRdvWidgetEnabled(!!s?.widget_enabled);
            setRdvWidgetLabel(s?.widget_label || '');
        }).catch(() => {}).finally(() => setRdvWidgetLoading(false));
    }, [canControlRdvWidget]);
    const saveRdvWidget = async (patch: { widget_enabled?: boolean; widget_label?: string }) => {
        setRdvWidgetSaving(true);
        try {
            await moduleApi.saveProRdvSettings(patch);
        } catch (e) {
            console.error('Failed to save widget settings', e);
        } finally {
            setRdvWidgetSaving(false);
        }
    };

    const makeSnippet = (mode: 'prestation' | 'rdv-pro', label: string) => ownerId
        ? `<script src="${window.location.origin}/booking-widget.js" data-client-id="${ownerId}" data-mode="${mode}" data-label="${label.replace(/"/g, '&quot;')}"></script>`
        : '';

    const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
    const handleCopy = async (key: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
        } catch (e) {}
    };

    if (!isPrestation && !(canControlRdvWidget && !rdvWidgetLoading)) return null;

    return (
        <div className="max-w-md space-y-4 mt-8">
            {isPrestation && (
                <WidgetCard
                    title="Widget de réservation en ligne"
                    description="Ajoutez un bouton sur votre propre site — vos visiteurs réservent sans quitter votre page."
                    snippet={makeSnippet('prestation', 'Réserver en ligne')}
                    copied={copiedKey === 'prestation'}
                    onCopy={() => handleCopy('prestation', makeSnippet('prestation', 'Réserver en ligne'))}
                />
            )}
            {canControlRdvWidget && !rdvWidgetLoading && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <Globe size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-content text-sm">Widget de réservation en ligne{isRestauration ? ' (RDV Pro)' : ''}</p>
                            <p className="text-xs text-content-muted mt-0.5">
                                {isAdmin
                                    ? 'Un bouton flottant sur votre propre site pour fixer des rendez-vous avec de potentiels futurs clients.'
                                    : 'Ajoutez un bouton sur votre propre site — vos visiteurs demandent un rendez-vous sans quitter votre page. Désactivez-le à tout moment sans avoir à retirer le code de votre site.'}
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input type="checkbox" checked={rdvWidgetEnabled} className="sr-only peer"
                                onChange={(e) => { setRdvWidgetEnabled(e.target.checked); saveRdvWidget({ widget_enabled: e.target.checked }); }} />
                            <div className="w-10 h-6 bg-surface rounded-full border border-border peer-checked:bg-brand-600 transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                        </label>
                    </div>
                    {rdvWidgetEnabled && (
                        <>
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Texte du bouton</label>
                                <div className="flex items-center gap-2">
                                    <input type="text" value={rdvWidgetLabel} onChange={(e) => setRdvWidgetLabel(e.target.value)}
                                        onBlur={() => saveRdvWidget({ widget_label: rdvWidgetLabel })}
                                        placeholder="Réserver en ligne" maxLength={60}
                                        className="flex-1 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                    {rdvWidgetSaving && <Loader2 size={14} className="animate-spin text-content-muted shrink-0" />}
                                </div>
                            </div>
                            <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-2">
                                <Code2 size={14} className="text-content-muted shrink-0" />
                                <code className="text-[11px] text-content-muted break-all font-mono flex-1">{makeSnippet('rdv-pro', rdvWidgetLabel || 'Réserver en ligne')}</code>
                            </div>
                            <p className="text-xs text-content-muted">Collez ce code juste avant la balise <code className="text-[11px] bg-surface border border-border px-1 py-0.5 rounded">&lt;/body&gt;</code> de votre site.{isAdmin ? ' Configurez vos horaires et votre durée de rendez-vous dans l\'onglet "RDV Pro".' : ''}</p>
                            <button onClick={() => handleCopy('rdv-widget', makeSnippet('rdv-pro', rdvWidgetLabel || 'Réserver en ligne'))}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                                {copiedKey === 'rdv-widget' ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier le code</>}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// --- Onglet Horaires (client Prestation uniquement) : bornes de la frise horaire du Planning
// rendez-vous — déplacé ici depuis le modal "gear" du module Planning pour éviter le doublon avec
// cette page Paramètres centrale (même principe que Codes Absences). ---
function PlanningRdvHoursTab() {
    const { refreshUser } = useAuth();
    const [draft, setDraft] = React.useState<WeeklyHours>(defaultWeeklyHours());
    const [locationMode, setLocationMode] = React.useState<'domicile' | 'entreprise' | 'les_deux'>('domicile');
    const [billingSettings, setBillingSettings] = React.useState<Record<string, any>>({});
    const [kmFeeEnabled, setKmFeeEnabled] = React.useState(false);
    const [kmFeePricePerKm, setKmFeePricePerKm] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        Promise.all([
            moduleApi.getPrestationPlanningSettings(),
            moduleApi.getBillingSettings(),
        ]).then(([settings, billing]: any[]) => {
            if (settings?.weekly_hours) setDraft({ ...defaultWeeklyHours(), ...settings.weekly_hours });
            if (settings?.location_mode) setLocationMode(settings.location_mode);
            setBillingSettings(billing || {});
            setKmFeeEnabled(!!billing?.km_fee_enabled);
            setKmFeePricePerKm(billing?.km_fee_price_per_km ? String(billing.km_fee_price_per_km) : '');
        }).catch((e: any) => console.error('Failed to load planning rdv hours', e)).finally(() => setLoading(false));
    }, []);

    const updateDay = (day: DayKey, patch: Partial<WeeklyHours[DayKey]>) => {
        setDraft((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
    };

    const showKmFee = locationMode === 'domicile' || locationMode === 'les_deux';

    const handleSave = async () => {
        setSaving(true);
        try {
            await moduleApi.savePrestationPlanningSettings({ weekly_hours: draft, location_mode: locationMode });
            // billing-settings est un enregistrement complet (PUT remplace toute la ligne) — on repart de
            // ce qui a été chargé pour ne pas écraser les autres réglages de facturation (catalogue, TVA...).
            await moduleApi.saveBillingSettings({
                ...billingSettings,
                km_fee_enabled: showKmFee && kmFeeEnabled,
                km_fee_price_per_km: Math.max(0, Number(kmFeePricePerKm) || 0),
            });
            await refreshUser();
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save planning rdv hours', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-6">
            <div className="space-y-2">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Mode d'intervention</label>
                <p className="text-xs text-content-muted italic">Détermine si vous devez préciser le lieu (chez le client ou dans l'entreprise) à chaque rendez-vous, ou si c'est toujours le même.</p>
                <div className="grid grid-cols-3 gap-2">
                    {([
                        { key: 'domicile', label: 'Chez le client' },
                        { key: 'entreprise', label: "Dans l'entreprise" },
                        { key: 'les_deux', label: 'Les deux' },
                    ] as const).map((opt) => (
                        <button key={opt.key} type="button" onClick={() => setLocationMode(opt.key)}
                            className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${locationMode === opt.key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {showKmFee && (
                <div className="space-y-2 p-4 rounded-2xl bg-surface-soft border border-border">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={kmFeeEnabled} onChange={(e) => setKmFeeEnabled(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                        <span className="text-xs font-bold text-content-muted uppercase tracking-widest">Facturer des frais kilométriques</span>
                    </label>
                    <p className="text-xs text-content-muted italic">Calculés automatiquement à la clôture d'un rendez-vous "chez le client", selon la distance routière entre votre établissement et son adresse.</p>
                    {kmFeeEnabled && (
                        <div className="flex items-center gap-2 pt-1">
                            <input type="number" min={0} step="0.01" value={kmFeePricePerKm} onChange={(e) => setKmFeePricePerKm(e.target.value)}
                                placeholder="Ex: 0.50" className="w-32 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            <span className="text-xs text-content-muted">€ / km</span>
                        </div>
                    )}
                </div>
            )}

            <p className="text-xs text-content-muted italic">Ces horaires bornent la frise horaire affichée dans le planning journalier, jour par jour.</p>
            <div className="space-y-2">
                {DAY_KEYS.map((day) => {
                    const cfg = draft[day];
                    return (
                        <div key={day} className="p-2.5 rounded-xl bg-surface-soft border border-border space-y-2">
                            <div className="flex items-center gap-3">
                                <span className="w-24 shrink-0 text-xs font-bold text-content">{DAY_LABELS[day]}</span>
                                <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                    <input type="checkbox" checked={cfg.closed} onChange={(e) => updateDay(day, { closed: e.target.checked })} className="accent-brand-600" />
                                    Fermé
                                </label>
                                {!cfg.closed && (
                                    <>
                                        <input type="time" value={cfg.opening} onChange={(e) => updateDay(day, { opening: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        <input type="time" value={cfg.closing} onChange={(e) => updateDay(day, { closing: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                    </>
                                )}
                            </div>
                            {!cfg.closed && (
                                <div className="flex items-center gap-3 pl-[calc(6rem+0.75rem)]">
                                    <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                        <input type="checkbox" checked={!!cfg.hasBreak}
                                            onChange={(e) => updateDay(day, e.target.checked
                                                ? { hasBreak: true, breakStart: cfg.breakStart || '12:00', breakEnd: cfg.breakEnd || '14:00' }
                                                : { hasBreak: false })}
                                            className="accent-brand-600" />
                                        Inclure une pause
                                    </label>
                                    {cfg.hasBreak && (
                                        <>
                                            <input type="time" value={cfg.breakStart || ''} onChange={(e) => updateDay(day, { breakStart: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                            <span className="text-xs text-content-muted">–</span>
                                            <input type="time" value={cfg.breakEnd || ''} onChange={(e) => updateDay(day, { breakEnd: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>
        </div>
    );
}

// RDV Professionnel (léger) : un seul calendrier établissement, sans catalogue ni employés — proposé
// depuis une réponse à une "Documentation Établissement" (ex: privatisation). Réutilise la même structure
// WeeklyHours/DayHours que le Planning Prestation, mais dans une colonne dédiée (pro_rdv_weekly_hours).
export function ProRdvHoursTab() {
    const [draft, setDraft] = React.useState<WeeklyHours>(defaultWeeklyHours());
    const [employees, setEmployees] = React.useState<any[]>([]);
    const [eligibleIds, setEligibleIds] = React.useState<string[]>([]);
    const [employeeSearch, setEmployeeSearch] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        Promise.all([moduleApi.getProRdvSettings(), moduleApi.getEmployes().catch(() => [])]).then(([settings, emps]: any[]) => {
            if (settings?.weekly_hours) setDraft({ ...defaultWeeklyHours(), ...settings.weekly_hours });
            setEligibleIds(Array.isArray(settings?.eligible_employee_ids) ? settings.eligible_employee_ids.map(String) : []);
            setEmployees(Array.isArray(emps) ? emps : []);
        }).catch((e: any) => console.error('Failed to load pro rdv settings', e)).finally(() => setLoading(false));
    }, []);

    const updateDay = (day: DayKey, patch: Partial<WeeklyHours[DayKey]>) => {
        setDraft((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
    };

    const employeeName = (emp: any) => `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Salarié';
    const eligibleEmployees = employees.filter((e) => eligibleIds.includes(String(e.id)));
    const searchResults = employeeSearch.trim().length > 0
        ? employees.filter((e) => !eligibleIds.includes(String(e.id)) && employeeName(e).toLowerCase().includes(employeeSearch.trim().toLowerCase())).slice(0, 8)
        : [];
    const addEligible = (id: string) => { setEligibleIds((prev) => [...prev, id]); setEmployeeSearch(''); };
    const removeEligible = (id: string) => setEligibleIds((prev) => prev.filter((e) => e !== id));

    const handleSave = async () => {
        setSaving(true);
        try {
            await moduleApi.saveProRdvSettings({ weekly_hours: draft, eligible_employee_ids: eligibleIds });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save pro rdv settings', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-6">
            <p className="text-xs text-content-muted italic">Ces horaires définissent les créneaux proposés à vos contacts lorsqu'une "Démarche client" leur permet de prendre rendez-vous (ex: privatisation). La durée d'un rendez-vous se configure directement dans chaque "Démarche client".</p>

            <div className="space-y-2">
                {DAY_KEYS.map((day) => {
                    const cfg = draft[day];
                    return (
                        <div key={day} className="p-2.5 rounded-xl bg-surface-soft border border-border space-y-2">
                            <div className="flex items-center gap-3">
                                <span className="w-24 shrink-0 text-xs font-bold text-content">{DAY_LABELS[day]}</span>
                                <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                    <input type="checkbox" checked={cfg.closed} onChange={(e) => updateDay(day, { closed: e.target.checked })} className="accent-brand-600" />
                                    Fermé
                                </label>
                                {!cfg.closed && (
                                    <>
                                        <input type="time" value={cfg.opening} onChange={(e) => updateDay(day, { opening: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        <input type="time" value={cfg.closing} onChange={(e) => updateDay(day, { closing: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                    </>
                                )}
                            </div>
                            {!cfg.closed && (
                                <div className="flex items-center gap-3 pl-[calc(6rem+0.75rem)]">
                                    <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                        <input type="checkbox" checked={!!cfg.hasBreak}
                                            onChange={(e) => updateDay(day, e.target.checked
                                                ? { hasBreak: true, breakStart: cfg.breakStart || '12:00', breakEnd: cfg.breakEnd || '14:00' }
                                                : { hasBreak: false })}
                                            className="accent-brand-600" />
                                        Inclure une pause
                                    </label>
                                    {cfg.hasBreak && (
                                        <>
                                            <input type="time" value={cfg.breakStart || ''} onChange={(e) => updateDay(day, { breakStart: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                            <span className="text-xs text-content-muted">–</span>
                                            <input type="time" value={cfg.breakEnd || ''} onChange={(e) => updateDay(day, { breakEnd: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="space-y-2">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Salariés qui font les RDV Pro</label>
                <p className="text-xs text-content-muted italic">Ceux que vous pourrez assigner à un rendez-vous depuis le calendrier RDV Pro.</p>
                {employees.length === 0 ? (
                    <p className="text-xs text-content-muted">Aucun salarié disponible.</p>
                ) : (
                    <>
                        {eligibleEmployees.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {eligibleEmployees.map((emp: any) => (
                                    <span key={emp.id} className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-bold border border-brand-500/20">
                                        {employeeName(emp)}
                                        <button onClick={() => removeEligible(String(emp.id))} className="p-0.5 rounded hover:bg-brand-500/20"><X size={11} /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="relative">
                            <input type="text" value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)}
                                placeholder="Rechercher un salarié à ajouter..."
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                            {searchResults.length > 0 && (
                                <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-xl shadow-xl max-h-56 overflow-y-auto">
                                    {searchResults.map((emp: any) => (
                                        <button key={emp.id} type="button" onMouseDown={() => addEligible(String(emp.id))}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-surface-soft transition-colors text-content">
                                            {employeeName(emp)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>
        </div>
    );
}

type TabKey = 'compte' | 'facturation' | 'absences' | 'horaires' | 'notifications' | 'applications' | 'documentation';

export const SettingsPage: React.FC = () => {
    const { user } = useAuth();
    const location = useLocation();
    const initialTab = (location.state as { tab?: TabKey } | null)?.tab;
    const [tab, setTab] = React.useState<TabKey>(initialTab || 'compte');
    const [activeModules, setActiveModules] = React.useState<string[]>([]);
    const [modulesLoaded, setModulesLoaded] = React.useState(false);

    React.useEffect(() => {
        authApi.getMyModules().then((modules: any) => {
            const names = (Array.isArray(modules) ? modules : [])
                .filter((m: any) => m.is_active === 1)
                .map((m: any) => m.module_name);
            setActiveModules(names);
        }).catch((e) => console.error('Failed to load active modules', e)).finally(() => setModulesLoaded(true));
    }, []);

    // Facturation/Codes Absences/Notifications : pertinents pour un client ou un collaborateur (pas un
    // salarié, qui n'a pas à configurer ces réglages) — ET seulement si le module correspondant est
    // réellement actif pour l'établissement (même logique que le verrou serveur : un module désactivé
    // masque aussi ses réglages, pas seulement ses pages). Le "Codes Absences" partagé reste visible tant
    // qu'au moins un des 3 modules qui l'utilisent est actif (planning, planning_rdv, demande_conges),
    // pour rester cohérent avec l'exemption appliquée côté serveur sur /planning/settings.
    // Les paramètres de modules (facturation, absences, notifications) sont réservés au client titulaire
    // du compte — un collaborateur ayant accès à un module au quotidien n'a pas accès à sa configuration.
    const canSeeBusinessTabs = user?.type === 'client';
    const hasModule = (name: string) => activeModules.includes(name);
    const canSeeFacturation = canSeeBusinessTabs && hasModule('facture');
    // Les codes d'absence vivent désormais dans les réglages du module Planning (demande d'Emeni,
    // 2026-08-25). Cet onglet ne subsiste que pour les clients SANS module Planning — Planning RDV ou
    // demandes de congés seuls — qui n'auraient sinon plus aucun endroit où les éditer. Un même client
    // ne le voit donc jamais aux deux endroits à la fois.
    const canSeeAbsences = canSeeBusinessTabs && !hasModule('planning')
        && (hasModule('planning_rdv') || hasModule('demande_conges'));
    // Horaires du Planning rendez-vous : Prestation uniquement (Restauration garde ses propres horaires
    // dans le module Planning, structure trop différente pour être fusionnée dans cette passe).
    const canSeeHoraires = canSeeBusinessTabs && user?.client_type === 'prestation' && hasModule('planning_rdv');
    // L'onglet Notifications combine deux blocs indépendants, chacun visible selon ses propres modules :
    // les emails du Planning rendez-vous (Prestation uniquement) et les emails Facture/Congés/Événementiel/
    // RDV Pro (les deux types de client, un par un selon le module actif — cf. ClientEmailNotificationsSettings).
    const canSeePrestationNotif = canSeeBusinessTabs && user?.client_type === 'prestation' && hasModule('planning_rdv');
    const canSeeClientEmailNotif = canSeeBusinessTabs && (hasModule('facture') || hasModule('demande_conges') || hasModule('evenementiel') || hasModule('crm') || (hasModule('rdv_pro') && user?.client_type === 'restauration'));
    const canSeeNotifications = canSeePrestationNotif || canSeeClientEmailNotif;
    // "Application" (connexion d'outils tiers, Stripe pour l'instant) : ouvert aux deux types de client
    // depuis l'ajout de "Démarches client" (acomptes/paiements possibles côté Restauration aussi). Corrigé
    // (bug remonté par le propriétaire) : la clé Stripe sert aussi bien aux Démarches client (module "crm",
    // acompte/paiement intégral configurable par modèle) qu'à la facturation — un établissement avec
    // Démarches actives mais Facture désactivée n'avait autrement aucun moyen de connecter Stripe.
    const canSeeApplications = canSeeBusinessTabs && (hasModule('facture') || hasModule('crm'));
    // Démarches client : liée aux contacts CRM, disponible pour les deux types de client dès que le
    // module CRM est actif. L'historique des demandes envoyées ("Demandes récurrentes") a été déplacé
    // hors des Réglages vers une tuile dédiée sur le Dashboard (App.tsx > DashboardView).
    const canSeeDocuments = canSeeBusinessTabs && hasModule('crm');

    const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
        { key: 'compte', label: 'Compte', icon: <UserIcon size={14} /> },
        ...(canSeeFacturation ? [{ key: 'facturation' as TabKey, label: 'Facturation', icon: <Receipt size={14} /> }] : []),
        ...(canSeeAbsences ? [{ key: 'absences' as TabKey, label: 'Codes Absences', icon: <CalendarClock size={14} /> }] : []),
        ...(canSeeHoraires ? [{ key: 'horaires' as TabKey, label: 'Horaires', icon: <Clock size={14} /> }] : []),
        ...(canSeeNotifications ? [{ key: 'notifications' as TabKey, label: 'Notifications', icon: <Bell size={14} /> }] : []),
        ...(canSeeApplications ? [{ key: 'applications' as TabKey, label: 'Application', icon: <Plug size={14} /> }] : []),
        ...(canSeeDocuments ? [{ key: 'documentation' as TabKey, label: 'Démarches client', icon: <FileText size={14} /> }] : []),
    ];

    // Si l'onglet actuellement sélectionné devient indisponible (modules pas encore chargés au premier
    // rendu, puis un tab non "compte" s'avère non autorisé une fois chargés), on revient sur Compte.
    React.useEffect(() => {
        if (modulesLoaded && tab !== 'compte' && !tabs.some((t) => t.key === tab)) {
            setTab('compte');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modulesLoaded, canSeeFacturation, canSeeAbsences, canSeeHoraires, canSeeNotifications, canSeeApplications, canSeeDocuments]);

    return (
        <div className="space-y-8">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><Settings size={24} /> Paramètres</h1>
                <p className="text-content-muted mt-1">Réglages de votre compte et de votre établissement.</p>
            </header>

            <div className="bg-surface border border-border rounded-2xl p-2 inline-flex flex-wrap gap-2">
                {tabs.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === t.key ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'compte' && <AccountTab />}
            {tab === 'facturation' && canSeeFacturation && <BillingSettingsModal open embedded onClose={() => {}} />}
            {tab === 'absences' && canSeeAbsences && <AbsenceCodesSettings />}
            {tab === 'horaires' && canSeeHoraires && <PlanningRdvHoursTab />}
            {tab === 'notifications' && canSeeNotifications && (
                <div className="space-y-10">
                    {canSeePrestationNotif && <PrestationNotificationsSettings activeModules={activeModules} />}
                    {canSeeClientEmailNotif && <ClientEmailNotificationsSettings activeModules={activeModules} clientType={user?.client_type} />}
                </div>
            )}
            {tab === 'applications' && canSeeApplications && <ApplicationsSettings />}
            {tab === 'documentation' && canSeeDocuments && <DocumentTemplatesSettings canOfferAppointment={(hasModule('rdv_pro') && user?.client_type === 'restauration') || (hasModule('planning_rdv') && user?.client_type === 'prestation')} isPrestation={user?.client_type === 'prestation'} />}
        </div>
    );
};

export default SettingsPage;
